import {
    assertNonNull,
    BlockBuilder,
    EditorOperations,
    isTable,
    isTableCell,
    isTableRow,
    Table,
    TableCell,
    TableRow,
    TABLE_CONSTANTS,
} from '..';
import { BasePoint, Editor, Element, Node, NodeEntry, Path, Range, Transforms } from 'slate';
import { isNodeEntry } from '../types';
import { toNodes } from './NodeEntry';

function makeColumnPaths([table, tablePath]: NodeEntry<Table>, columnIndex: number): Path[] {
    const rows = table.children.length;
    return Array.from(new Array(rows)).map((_, i) => [...tablePath, i, columnIndex]);
}

export function getTableCellEntryByCoordinates(
    editor: Editor,
    [, tablePath]: NodeEntry<Table>,
    coordinates: [number, number],
): NodeEntry<TableCell> {
    const node = Editor.node(editor, [...tablePath, ...coordinates.slice().reverse()]);

    if (!isNodeEntry(node, isTableCell)) {
        throw new Error(
            `Node at coordinates ${JSON.stringify(coordinates)} is not a table cell: ${JSON.stringify(node, null, 2)}`,
        );
    }

    return node;
}

export function getLastTableRowEntry(editor: Editor, [, path]: NodeEntry<Table>) {
    const [, lastPath] = Editor.last(editor, path);
    const tableRowEntry = Editor.above(editor, { at: lastPath, match: isTableRow });
    assertNonNull(tableRowEntry, 'tableRowEntry should not be null');
    return tableRowEntry;
}

export function jumpToPreviousTableCell(editor: Editor, currentTableCellEntry: NodeEntry<TableCell>) {
    const previous = Editor.previous(editor, {
        at: currentTableCellEntry[1],
        match: isTableCell,
    });
    if (previous) {
        const [, path] = previous;
        Transforms.select(editor, Editor.end(editor, path));
    }
}

export function jumpToNextTableCell(editor: Editor, currentTableCellEntry: NodeEntry<TableCell>) {
    const next = Editor.next(editor, {
        at: currentTableCellEntry[1],
        match: isTableCell,
    });
    if (next) {
        const [, path] = next;
        Transforms.select(editor, Editor.end(editor, path));
    } else {
        const tableEntry = getTableEntry(editor, currentTableCellEntry);
        appendRow(editor, tableEntry);
    }
}

export function getPointBelowTableCell(editor: Editor, currentTableCellEntry: NodeEntry<TableCell>): BasePoint | null {
    const [table, tablePath] = getTableEntry(editor, currentTableCellEntry);
    const [, path] = currentTableCellEntry;
    const [columnIndex, rowIndex, ...firstPathPart] = path.slice().reverse();
    const nextRowIndex = rowIndex + 1;

    const newPath = [columnIndex, nextRowIndex, ...firstPathPart].reverse();
    if (nextRowIndex < table.children.length) {
        return Editor.end(editor, newPath);
    } else {
        const nextNodeEntry = Editor.next(editor, { at: tablePath });
        if (nextNodeEntry) {
            return Editor.start(editor, nextNodeEntry[1]);
        }
    }

    return null;
}

export function getPointAboveTableCell(editor: Editor, currentTableCellEntry: NodeEntry<TableCell>): BasePoint | null {
    const [, path] = currentTableCellEntry;
    const [columnIndex, rowIndex, ...firstPathPart] = path.slice().reverse();
    const prevRowIndex = rowIndex - 1;
    if (prevRowIndex >= 0) {
        const newPath = [columnIndex, prevRowIndex, ...firstPathPart].reverse();
        return Editor.end(editor, newPath);
    } else {
        const [, tablePath] = getTableEntry(editor, currentTableCellEntry);
        const previousNodeEntry = Editor.previous(editor, { at: tablePath });
        if (previousNodeEntry) {
            return Editor.end(editor, previousNodeEntry[1]);
        }
    }

    return null;
}

export function getEnclosingTableCellForCurrentSelection(editor: Editor): NodeEntry<TableCell> | null {
    if (editor.selection == null) {
        return null;
    }

    const tableCell = Editor.above(editor, {
        at: editor.selection,
        match: isTableCell,
        mode: 'lowest',
    });

    if (tableCell) {
        return tableCell;
    }

    const currentNodeEntry = Editor.node(editor, editor.selection);

    if (isNodeEntry(currentNodeEntry, isTableCell)) {
        return currentNodeEntry;
    }

    return null;
}

export function getTableRowEntryForCell(editor: Editor, [, path]: NodeEntry<TableCell>) {
    const tableRowEntry = Editor.above(editor, {
        at: path,
        match: isTableRow,
        mode: 'lowest',
    });
    assertNonNull(tableRowEntry, 'tableRowEntry should not be null');
    return tableRowEntry;
}

export function getTableEntry(editor: Editor, [node, path]: NodeEntry<Table | TableCell | TableRow>) {
    if (isTable(node)) {
        const node = Editor.node(editor, path);
        if (!isNodeEntry(node, isTable)) {
            throw new Error(`getTableEntry: Node ${JSON.stringify([node, path], null, 2)} is not a table.`);
        }
        return node;
    }

    const tableEntry = Editor.above(editor, {
        at: path,
        match: isTable,
        mode: 'lowest',
    });

    assertNonNull(
        tableEntry,
        `getTableEntry: No tableEntry found above entry ${JSON.stringify([node, path], null, 2)}`,
    );

    return tableEntry;
}

export function appendRow(editor: Editor, [table, tablePath]: NodeEntry<Table>) {
    const [, rows] = table.dimensions;
    insertRow(editor, [table, tablePath], rows);
}

export function appendColumn(editor: Editor, [table, tablePath]: NodeEntry<Table>) {
    const [columns] = table.dimensions;
    insertColumn(editor, [table, tablePath], columns);
}

export function insertRow(editor: Editor, [table, tablePath]: NodeEntry<Table>, index: number) {
    // based on the children of the row, we know how many columsn we need
    const newTableRow = BlockBuilder.tableRowByColumns(table.dimensions[0]);

    Editor.withoutNormalizing(editor, () => {
        const path = [...tablePath, index];
        Transforms.insertNodes(editor, [newTableRow], { at: path });
        recalculateAndSetTableDimensions(editor, [table, tablePath]);
        // Finally we set the selection in the first cell of the new row
        Transforms.select(editor, [...path, 0]);
    });
}

export function insertRowAfterTableCell(editor: Editor, tableCellEntry: NodeEntry<TableCell>) {
    const tableEntry = getTableEntry(editor, tableCellEntry);
    const [, path] = getTableRowEntryForCell(editor, tableCellEntry);
    insertRow(editor, tableEntry, path[path.length - 1] + 1);
}

export function insertRowBeforeTableCell(editor: Editor, tableCellEntry: NodeEntry<TableCell>) {
    const tableEntry = getTableEntry(editor, tableCellEntry);
    const [, path] = getTableRowEntryForCell(editor, tableCellEntry);
    insertRow(editor, tableEntry, path[path.length - 1]);
}

export function recalculateAndSetTableDimensions(editor: Editor, [table, path]: NodeEntry<Table>): [number, number] {
    // Get the current table node to ensure we have the latest columnWidths
    const currentTable = Node.get(editor, path) as Table;

    const rows = toNodes([...Node.children(editor, path)]).filter(isTableRow);
    const maxColumnsLength = rows.reduce((acc, val) => Math.max(acc, val.children.length), 0);
    const dimensions: [number, number] = [maxColumnsLength, rows.length];

    // Sync columnWidths with new dimensions
    const currentWidths = currentTable.columnWidths || [];
    const targetColumns = maxColumnsLength;

    if (currentWidths.length !== targetColumns) {
        const newWidths = Array(targetColumns).fill(TABLE_CONSTANTS.DEFAULT_COLUMN_WIDTH);

        // Preserve existing widths where possible
        for (let i = 0; i < Math.min(currentWidths.length, targetColumns); i++) {
            newWidths[i] = currentWidths[i];
        }

        Transforms.setNodes(editor, { dimensions, columnWidths: newWidths }, { match: isTable, at: path });
    } else {
        Transforms.setNodes(editor, { dimensions }, { match: isTable, at: path });
    }

    return dimensions;
}

export function normalizeTableCells(editor: Editor, [table, path]: NodeEntry<Table>): boolean {
    const rowEntries = [...Node.children(editor, path)].filter((entry): entry is NodeEntry<TableRow> =>
        isNodeEntry(entry, isTableRow),
    );

    const [columns, rows] = table.dimensions;

    // We need to keep track if any normalization happend at all to return this information
    // this is needed for the normalization
    let normalized = false;

    Editor.withoutNormalizing(editor, () => {
        rowEntries.forEach((rowEntry) => {
            const [row, rowPath] = rowEntry;
            const differenceToColumns = columns - row.children.length;
            if (differenceToColumns > 0) {
                const lastColumnPath = [
                    // the path of the row
                    ...rowPath,
                    // the index of the last column
                    row.children.length - 1,
                ];

                // We fill up the row with the missing columns based on the difference
                Transforms.insertNodes(editor, BlockBuilder.tableCells(differenceToColumns), {
                    at: Path.next(lastColumnPath),
                });
                normalized = true;
            }
        });

        const differenceToRows = rows - rowEntries.length;
        if (differenceToRows > 0) {
            const rows = BlockBuilder.tableRowsByColumns(columns, differenceToRows);
            const [, lastRowPath] = getLastTableRowEntry(editor, [table, path]);
            Transforms.insertNodes(editor, rows, {
                at: Path.next(lastRowPath),
            });
            normalized = true;
        }
    });

    return normalized;
}

export function insertColumn(editor: Editor, [table, tablePath]: NodeEntry<Table>, index: number) {
    Editor.withoutNormalizing(editor, () => {
        const columnPaths = makeColumnPaths([table, tablePath], index);

        // For each row, we need to insert a new column, so we iterate
        columnPaths.forEach((path, i) => {
            Transforms.insertNodes(editor, [BlockBuilder.tableCell()], {
                at: path,
            });

            if (i == 0) {
                Transforms.select(editor, path);
            }
        });

        // Update column widths array
        const newColumnWidths = [...(table.columnWidths || [])];
        newColumnWidths.splice(index, 0, TABLE_CONSTANTS.DEFAULT_COLUMN_WIDTH);
        Transforms.setNodes(editor, { columnWidths: newColumnWidths }, { at: tablePath });

        recalculateAndSetTableDimensions(editor, [table, tablePath]);
    });
}

export function insertColumnAfterTableCell(editor: Editor, tableCellEntry: NodeEntry<TableCell>) {
    const tableEntry = getTableEntry(editor, tableCellEntry);
    const columnIndex = getColumnIndex(tableCellEntry);
    insertColumn(editor, tableEntry, columnIndex + 1);
}

export function insertColumnBeforeTableCell(editor: Editor, tableCellEntry: NodeEntry<TableCell>) {
    const tableEntry = getTableEntry(editor, tableCellEntry);
    const columnIndex = getColumnIndex(tableCellEntry);
    insertColumn(editor, tableEntry, Math.max(0, columnIndex));
}

export function deleteColumnByIndex(editor: Editor, [table, tablePath]: NodeEntry<Table>, columnIndex: number) {
    Editor.withoutNormalizing(editor, () => {
        const columnPaths = makeColumnPaths([table, tablePath], columnIndex);

        columnPaths.forEach((path) => {
            Transforms.delete(editor, { at: path });
        });

        // Update column widths array
        if (table.columnWidths) {
            const newColumnWidths = [...table.columnWidths];
            newColumnWidths.splice(columnIndex, 1);
            Transforms.setNodes(editor, { columnWidths: newColumnWidths }, { at: tablePath });
        }

        recalculateAndSetTableDimensions(editor, [table, tablePath]);
        const firstCellPath = [...tablePath, 0, Math.max(0, columnIndex - 1)];
        EditorOperations.Transforms.selectPathIfExists(editor, firstCellPath);
    });
}

export function deleteRow(editor: Editor, [tableRow, tableRowPath]: NodeEntry<TableRow>) {
    Editor.withoutNormalizing(editor, () => {
        // we need to get the table before we remove the nodes, otherwise it blows up because it
        // cannot find the row anymore
        const tableEntry = getTableEntry(editor, [tableRow, tableRowPath]);
        Transforms.removeNodes(editor, { at: tableRowPath, match: isTableRow });
        const [columns] = recalculateAndSetTableDimensions(editor, tableEntry);

        if (tableRowPath[tableRowPath.length - 1] > 0) {
            const lastCellPath = [...Path.previous(tableRowPath), Math.max(0, columns - 1)];
            EditorOperations.Transforms.selectPathIfExists(editor, lastCellPath, 'end');
        }
    });
}

export function isColumnSelected(editor: Editor, [, tableCellPath]: NodeEntry<TableCell>, columnIndex: number) {
    return (
        editor.selection &&
        Range.isCollapsed(editor.selection) &&
        Path.common(editor.selection.focus.path, tableCellPath).length > 0 &&
        editor.selection.focus.path[tableCellPath.length - 1] === columnIndex
    );
}

export function isRowSelected(editor: Editor, [, tableRowPath]: NodeEntry<TableRow>) {
    if (!editor.selection || !Range.isCollapsed(editor.selection)) {
        return false;
    }

    // Check if the selection is within this row
    const selectionPath = editor.selection.focus.path;
    const rowDepth = tableRowPath.length;

    // The selection must be deeper than the row (inside a cell)
    if (selectionPath.length <= rowDepth) {
        return false;
    }

    // Check if the selection path includes this row
    return Path.isAncestor(tableRowPath, selectionPath);
}

export function getColumnIndex([, tableCellPath]: NodeEntry<TableCell>) {
    return tableCellPath[tableCellPath.length - 1];
}

export function isWithinFirstRow([, tableCellPath]: NodeEntry<TableCell>) {
    const rowPath = Path.parent(tableCellPath);
    const last = rowPath[rowPath.length - 1];
    return last === 0;
}

export function moveColumn(editor: Editor, [table, tablePath]: NodeEntry<Table>, fromIndex: number, toIndex: number) {
    Editor.withoutNormalizing(editor, () => {
        const columnPaths = makeColumnPaths([table, tablePath], fromIndex);
        const cells = columnPaths.map((path) => Node.get(editor, path));

        // Save the width of the column being moved
        const movedWidth = table.columnWidths?.[fromIndex] || TABLE_CONSTANTS.DEFAULT_COLUMN_WIDTH;

        // Delete the original column
        deleteColumnByIndex(editor, [table, tablePath], fromIndex);

        // Get updated table after deletion
        const updatedTable = Node.get(editor, tablePath) as Table;

        // Insert the column at the new position
        insertColumn(editor, [updatedTable, tablePath], toIndex);

        // Replace the empty cells with the moved cells
        const newColumnPaths = makeColumnPaths([updatedTable, tablePath], toIndex);
        newColumnPaths.forEach((path, i) => {
            Transforms.removeNodes(editor, { at: path });
            Transforms.insertNodes(editor, cells[i], { at: path });
        });

        // Update column widths to restore the moved column's width
        if (updatedTable.columnWidths) {
            const newColumnWidths = [...updatedTable.columnWidths];
            newColumnWidths[toIndex] = movedWidth;
            Transforms.setNodes(editor, { columnWidths: newColumnWidths }, { at: tablePath });
        }

        recalculateAndSetTableDimensions(editor, [updatedTable, tablePath]);
        const firstCellPath = [...tablePath, 0, toIndex];
        EditorOperations.Transforms.selectPathIfExists(editor, firstCellPath);
    });
}

export function moveRow(editor: Editor, [table, tablePath]: NodeEntry<Table>, fromIndex: number, toIndex: number) {
    Editor.withoutNormalizing(editor, () => {
        const rowPath = [...tablePath, fromIndex];
        const rowNode = Node.get(editor, rowPath);

        if (!isTableRow(rowNode)) {
            // This should ideally not happen if logic is correct
            console.error('Attempted to move a non-row element:', rowNode);
            return;
        }

        Transforms.moveNodes(editor, {
            at: rowPath,
            to: [...tablePath, toIndex],
        });

        // Select the first cell of the moved row
        const firstCellPath = [...tablePath, toIndex, 0];
        EditorOperations.Transforms.selectPathIfExists(editor, firstCellPath);
        // Recalculate dimensions, though moving rows shouldn't change them unless it's a bug elsewhere
        recalculateAndSetTableDimensions(editor, [table, tablePath]);
    });
}

/**
 * Interface for resolving cell text for sorting
 * Allows custom implementations to provide text extraction logic
 */
export interface CellTextResolver {
    getCellSortText(cell: Node): string;
}

/**
 * Default implementation that extracts plain text from cells
 */
export const defaultCellTextResolver: CellTextResolver = {
    getCellSortText(cell: Node): string {
        if (!Element.isElement(cell) || !cell.children.length) {
            return '';
        }

        const firstBlock = cell.children[0];

        // For non-element nodes, return their string content
        if (!Element.isElement(firstBlock)) {
            return Node.string(firstBlock).trim();
        }

        // Get text from first 2 children
        return firstBlock.children
            .slice(0, TABLE_CONSTANTS.MAX_CHILDREN_FOR_SORT_TEXT)
            .map((child) => Node.string(child))
            .join('')
            .trim();
    },
};

export function sortTableByColumn(
    editor: Editor,
    tableEntry: NodeEntry<Table>,
    columnIndex: number,
    direction: 'asc' | 'desc',
    textResolver?: CellTextResolver,
) {
    const [table, tablePath] = tableEntry;

    // Skip if table has only header row or no rows
    if (table.children.length <= TABLE_CONSTANTS.HEADER_ROW_COUNT) {
        return;
    }

    // Validate column index
    if (columnIndex < 0 || columnIndex >= table.dimensions[0]) {
        console.warn(`Invalid column index ${columnIndex} for table with ${table.dimensions[0]} columns`);
        return;
    }

    // Safety check from review: Validate all rows have the required column before sorting
    for (let i = TABLE_CONSTANTS.HEADER_ROW_COUNT; i < table.children.length; i++) {
        const row = table.children[i];
        if (!isTableRow(row) || !row.children[columnIndex]) {
            console.error(`Table is malformed. Row at index ${i} is missing column ${columnIndex}. Aborting sort.`);
            return;
        }
    }

    // Use provided resolver or default
    const resolver = textResolver || defaultCellTextResolver;

    Editor.withoutNormalizing(editor, () => {
        // Get all data rows except the header (first row)
        const dataRows = table.children.slice(TABLE_CONSTANTS.HEADER_ROW_COUNT);

        // Create sortable array with row index and sort key
        const sortableRows = dataRows.map((row, index) => {
            const cell = isTableRow(row) ? row.children[columnIndex] : undefined;
            const text = cell ? resolver.getCellSortText(cell) : '';
            return {
                index,
                text,
                row,
            };
        });

        // Sort rows by cell text
        sortableRows.sort((a, b) => {
            // Empty strings go to the end
            if (!a.text && !b.text) return 0;
            if (!a.text) return 1;
            if (!b.text) return -1;

            const compare = a.text.localeCompare(b.text, undefined, {
                numeric: true,
                sensitivity: 'base',
            });
            return direction === 'asc' ? compare : -compare;
        });

        // Remove all data rows (keeping header)
        for (let i = dataRows.length; i > 0; i--) {
            Transforms.removeNodes(editor, {
                at: [...tablePath, i],
            });
        }

        // Insert all sorted rows in a single operation
        const sortedRowNodes = sortableRows.map((item) => item.row);
        Transforms.insertNodes(editor, sortedRowNodes, {
            at: [...tablePath, TABLE_CONSTANTS.HEADER_ROW_COUNT], // Insert after header
        });
    });
}

/**
 * Duplicates a row below the specified row index
 */
export function duplicateRow(editor: Editor, [table, tablePath]: NodeEntry<Table>, rowIndex: number) {
    const row = table.children[rowIndex];

    if (!isTableRow(row)) {
        console.error('Cannot duplicate non-row element at index', rowIndex);
        return;
    }

    // Validate row index
    if (rowIndex < 0 || rowIndex >= table.children.length) {
        console.error('Invalid row index for duplication:', rowIndex);
        return;
    }

    Editor.withoutNormalizing(editor, () => {
        // Create new cells with copied content
        const newCells = row.children.map((cell) => {
            if (!isTableCell(cell)) {
                // If somehow we have a non-cell in a row, create an empty cell
                return BlockBuilder.tableCell();
            }

            // Create a new cell with cloned content
            const clonedContent = cell.children.map((child) => JSON.parse(JSON.stringify(child)));
            const newCell = BlockBuilder.tableCell(clonedContent);

            // Copy all cell properties (not just backgroundColor)
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { id, children, type, ...cellProperties } = cell as any;
            Object.assign(newCell, cellProperties);

            return newCell;
        });

        // Create new row with the copied cells
        const newRow = BlockBuilder.tableRow(newCells);

        // Insert the new row
        const insertPath = [...tablePath, rowIndex + 1];
        Transforms.insertNodes(editor, [newRow], { at: insertPath });

        // Place cursor at the end of the first cell of the new row
        const firstCellPath = [...insertPath, 0];
        const endPoint = Editor.end(editor, firstCellPath);
        Transforms.select(editor, endPoint);
    });
}

/**
 * Duplicates a column to the right of the specified column index
 */
export function duplicateColumn(editor: Editor, [table, tablePath]: NodeEntry<Table>, columnIndex: number) {
    if (columnIndex < 0 || columnIndex >= table.dimensions[0]) {
        console.error('Invalid column index for duplication:', columnIndex);
        return;
    }

    Editor.withoutNormalizing(editor, () => {
        // Collect all cells from the source column before modifying the table
        const sourceCells: Array<{ cell: TableCell; rowIndex: number }> = [];

        table.children.forEach((row, rowIndex) => {
            if (isTableRow(row) && row.children[columnIndex] && isTableCell(row.children[columnIndex])) {
                sourceCells.push({
                    cell: row.children[columnIndex] as TableCell,
                    rowIndex,
                });
            }
        });

        // Insert a new empty column
        insertColumn(editor, [table, tablePath], columnIndex + 1);

        // Copy content to the new column
        sourceCells.forEach(({ cell, rowIndex }) => {
            const targetCellPath = [...tablePath, rowIndex, columnIndex + 1];

            // Get the newly created cell
            const targetCell = Node.get(editor, targetCellPath);

            if (!isTableCell(targetCell)) {
                console.warn('Target cell not found at path:', targetCellPath);
                return;
            }

            // Only proceed if source cell has content
            if (cell.children.length > 0) {
                // Remove the default empty paragraph from the new cell
                if (targetCell.children.length > 0) {
                    Transforms.removeNodes(editor, { at: [...targetCellPath, 0] });
                }

                // Clone and insert content
                const clonedContent = cell.children.map((child) => JSON.parse(JSON.stringify(child)));
                Transforms.insertNodes(editor, clonedContent, {
                    at: [...targetCellPath, 0],
                });
            }

            // Copy all cell properties (not just backgroundColor)
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { id, children, type, ...cellProperties } = cell as any;
            if (Object.keys(cellProperties).length > 0) {
                Transforms.setNodes(editor, cellProperties, { at: targetCellPath });
            }
        });

        // Column widths are already handled by insertColumn

        // Place cursor at the end of the first cell of new column
        const firstCellPath = [...tablePath, 0, columnIndex + 1];
        const endPoint = Editor.end(editor, firstCellPath);
        Transforms.select(editor, endPoint);
    });
}
